export const extractAndDelete = (obj, keyPath) => {
  // removes a deeply-nested attribute using dot notation
  const parts = keyPath.split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) {
      // attribute doesn't exist
      return undefined;
    }
    current = current[parts[i]];
  }
  const lastKey = parts[parts.length - 1];
  const value = current?.[lastKey];
  delete obj[parts[0]];
  return value?.toString(); // also returns value of given element for display purposes
};

export const formatDetailsKey = (key) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

let originalFetch = require("isomorphic-fetch");
export async function customFetch(url, retries, retryDelay) {
  let attempt = 0;
  while (attempt <= retries) {
    try {
      const response = await originalFetch(url);
      if (!response.ok) {
        // Checks if response status is 200-299
        throw new Error("Request failed with status " + response.status);
      }
      return response.text(); // Or parse JSON if expected
    } catch (error) {
      if (attempt === retries) {
        console.log(`Max retries reached for ${url}`);
        throw error;
      }
      console.log(`Retrying ${url}, attempt number ${attempt + 1}`);
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      attempt++;
    }
  }
}

export const getLocalStorageSAMLToken = () => {
  const now = Math.floor(Date.now() / 1000); // current time in seconds

  for (let key in localStorage) {
    if (
      key.includes("CognitoIdentityServiceProvider") &&
      key.endsWith("idToken")
    ) {
      const token = localStorage.getItem(key);

      try {
        const [, payloadBase64] = token.split(".");
        const payload = JSON.parse(atob(payloadBase64));
        if (payload.exp && payload.exp > now) {
          return token;
        }
      } catch (e) {
        continue;
      }
    }
  }

  return null; // No valid token found
};

export function extractUserInfoFromToken(token) {
  // Split the token into its parts
  const parts = token.split(".");

  if (parts.length !== 3) {
    throw new Error("Invalid JWT token format");
  }

  // Decode the payload part
  const payload = parts[1];
  let decodedPayload;
  try {
    decodedPayload = atob(payload);
  } catch (e) {
    throw new Error("Failed to decode token payload");
  }

  // Parse the decoded payload to get the identities
  let payloadObj;
  try {
    payloadObj = JSON.parse(decodedPayload);
  } catch (e) {
    throw new Error("Failed to parse token payload");
  }

  if (
    !payloadObj.identities ||
    !Array.isArray(payloadObj.identities) ||
    payloadObj.identities.length === 0
  ) {
    throw new Error("Identities not found or empty in token payload");
  }

  // Extract the first identity's userId as the email
  const email = payloadObj.identities[0].userId;
  if (!email) {
    throw new Error("Email not found in identities");
  }

  // Extract the username as the part before the '@' in the email
  const username = email.split("@")[0];
  if (!username) {
    throw new Error("Username could not be extracted from email");
  }

  return {
    username: username,
    email: email,
  };
}

/**
 * Generates and downloads a template file based on the provided configuration
 * @param {Object} config - The upload configuration object
 * @param {string} format - The format to download ('csv' or 'json')
 * @param {boolean} mandatoryOnly - Whether to include only mandatory fields
 * @param {string} filename - Optional filename (without extension)
 */
export const downloadTemplate = (
  config,
  format,
  mandatoryOnly,
  filename = null,
) => {
  const fields = mandatoryOnly
    ? config.fields.filter((field) => field.mandatory)
    : config.fields;

  const fileName = filename || `${config.name.replace(/\s+/g, "_")}_template`;

  if (format === "csv") {
    downloadCSV(fields, fileName, config.delimiter);
  } else if (format === "json") {
    downloadJSON(fields, fileName);
  }
};

export const downloadCSV = (
  fields,
  fileName,
  delimiter = ",",
  objects = [],
) => {
  // Create CSV header using the name property
  const header = fields.map((field) => field.name).join(delimiter);

  // Create CSV rows
  const rows =
    objects.length > 0
      ? objects.map((obj) =>
          fields
            .map((field) => {
              // Use field.value if exists, otherwise use field.name
              const propertyName = field.value || field.name;
              // Handle nested properties if needed
              const value = propertyName
                .split(".")
                .reduce((o, k) => (o || {})[k], obj);
              return value !== undefined
                ? `"${String(value).replace(/"/g, '""')}"`
                : "";
            })
            .join(delimiter),
        )
      : [fields.map(() => "").join(delimiter)]; // Empty row if no objects

  const csvContent = [header, ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadJSON = (fields, fileName, objects = []) => {
  // Create JSON content
  const jsonContent =
    objects.length > 0
      ? JSON.stringify(
          objects.map((obj) => {
            // Create a new object with the fields we want
            return fields.reduce((acc, field) => {
              // Use field.value if exists, otherwise use field.name
              const propertyName = field.value || field.name;
              // Handle nested properties if needed
              const value = propertyName
                .split(".")
                .reduce((o, k) => (o || {})[k], obj);
              acc[field.name] = value !== undefined ? value : "";
              return acc;
            }, {});
          }),
          null,
          2,
        )
      : JSON.stringify(
          [
            fields.reduce((acc, field) => {
              acc[field.name] = "";
              return acc;
            }, {}),
          ],
          null,
          2,
        );

  const blob = new Blob([jsonContent], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}.json`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
